import { useCallback, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { RefreshCcw, Eye, EyeOff, ShieldCheck, Radar, LineChart, Layers, ArrowRight } from "lucide-react";
import { apiGet } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

function formatErr(e) {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || "Something went wrong.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" ");
  return String(d);
}

// SVG grid, warm brown at low alpha — matches paper aesthetic
const GRID_BG =
  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='64' height='64' viewBox='0 0 64 64'><path d='M64 0H0V64' fill='none' stroke='rgba(127,95,82,0.09)' stroke-width='1'/></svg>\")";

const MonoMark = ({ size = 46 }) => (
  <div
    className="inline-flex items-center justify-center font-serif-display font-bold shrink-0 relative overflow-hidden"
    style={{
      width: size, height: size, borderRadius: size * 0.22,
      background: "#fff",
      border: "1px solid var(--cv-line)",
      boxShadow: "0 6px 16px rgba(127,95,82,0.14), inset 0 0 0 1px rgba(255,255,255,0.6)",
      fontSize: size * 0.52, letterSpacing: "-0.02em",
    }}
  >
    <span style={{ color: "var(--cv-crimson)", zIndex: 2 }}>C</span>
    <span style={{ color: "var(--cv-gold)", zIndex: 2 }}>V</span>
    <span
      style={{
        position: "absolute", left: 0, right: 0, bottom: 0,
        height: size * 0.13,
        background: "linear-gradient(90deg, var(--cv-crimson) 0%, var(--cv-orange) 55%, var(--cv-gold) 100%)",
      }}
    />
  </div>
);

const TrustChip = ({ icon: Icon, title, sub, color }) => (
  <div className="flex items-start gap-3">
    <div
      className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
      style={{ background: `${color}18`, border: `1px solid ${color}35` }}
    >
      <Icon className="w-5 h-5" style={{ color }} />
    </div>
    <div>
      <div className="text-[13.5px] font-bold" style={{ color: "var(--cv-charcoal)" }}>{title}</div>
      <div className="text-[12px]" style={{ color: "var(--cv-dark-grey)" }}>{sub}</div>
    </div>
  </div>
);

export default function Login() {
  const { login, verify2fa, user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const from = loc.state?.from || "/";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [captcha, setCaptcha] = useState(null);
  const [captchaAns, setCaptchaAns] = useState("");
  const [tempToken, setTempToken] = useState(null);
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const refreshCaptcha = useCallback(async () => {
    setCaptcha(null); setCaptchaAns("");
    const c = await apiGet("/auth/captcha"); setCaptcha(c);
  }, []);
  useEffect(() => { refreshCaptcha(); }, [refreshCaptcha]);
  useEffect(() => { if (user) nav(from, { replace: true }); }, [user, from, nav]);

  const submitLogin = async (e) => {
    e.preventDefault(); setErr(null); setBusy(true);
    try {
      const r = await login({ email, password, captcha_id: captcha.challenge_id, captcha_answer: captchaAns });
      if (r.requires_2fa) setTempToken(r.temp_token);
      else nav(from, { replace: true });
    } catch (e) { setErr(formatErr(e)); await refreshCaptcha(); }
    finally { setBusy(false); }
  };

  const submitOtp = async (e) => {
    e.preventDefault(); setErr(null); setBusy(true);
    try { await verify2fa({ temp_token: tempToken, code: otp }); nav(from, { replace: true }); }
    catch (e) { setErr(formatErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div
      className="min-h-screen relative"
      data-testid="login-page"
      style={{ background: "#ffffff" }}
    >
      {/* subtle warm grid overlay */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{ backgroundImage: GRID_BG, backgroundSize: "64px 64px", opacity: 0.5 }}
      />
      {/* top gradient accent stripe using the exact brand palette */}
      <div
        aria-hidden
        className="absolute top-0 left-0 right-0 h-[3px]"
        style={{
          background:
            "linear-gradient(90deg, transparent 0%, var(--cv-crimson) 22%, var(--cv-orange) 45%, var(--cv-yellow) 65%, var(--cv-gold) 82%, transparent 100%)",
        }}
      />

      <div className="relative z-10 min-h-screen grid grid-cols-1 lg:grid-cols-[1.15fr_1fr]">
        {/* -------------------------- Left: Brand Hero -------------------------- */}
        <div className="hidden lg:flex flex-col justify-between p-12 xl:p-16">
          <div className="flex items-center gap-3">
            <MonoMark size={48} />
            <div>
              <div className="text-[24px] font-serif-display font-bold leading-none" style={{ color: "var(--cv-charcoal)" }}>
                Cyber<span style={{ color: "var(--cv-crimson)" }}>Visible</span>
              </div>
              <div className="text-[10.5px] tracking-[0.24em] uppercase mt-1.5 font-bold" style={{ color: "var(--cv-dark-grey)" }}>
                Board Confidential · v2.0
              </div>
            </div>
          </div>

          <div className="max-w-[600px]">
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-6"
              style={{ background: "rgba(213,88,22,0.10)", border: "1px solid rgba(213,88,22,0.32)" }}
            >
              <span
                className="w-2 h-2 rounded-full"
                style={{ background: "var(--cv-orange)", boxShadow: "0 0 0 4px rgba(213,88,22,0.18)" }}
              />
              <span className="text-[11px] tracking-[0.22em] uppercase font-bold" style={{ color: "var(--cv-orange)" }}>
                Enterprise Cyber Posture
              </span>
            </div>

            <h1
              className="font-serif-display leading-[1.03]"
              style={{ fontSize: "clamp(42px, 4.8vw, 62px)", color: "var(--cv-charcoal)" }}
            >
              Your Unified Exposure<br />
              <span
                style={{
                  background: "linear-gradient(90deg, var(--cv-crimson) 0%, var(--cv-orange) 55%, var(--cv-yellow) 100%)",
                  WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent",
                }}
              >
                &amp; Threat Management
              </span><br />
              Platform.
            </h1>

            <p className="text-[15.5px] mt-6 max-w-[520px]" style={{ color: "var(--cv-dark-grey)", lineHeight: 1.6 }}>
              One board-grade view of identity, endpoint, network, vulnerability, SOC, email and cloud —
              with drill-down to the last leg and audit-ready evidence on every widget.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mt-10 max-w-[640px]">
              <TrustChip icon={Radar} title="Continuous Exposure" sub="assets · vulns · identity" color="#A5300F" />
              <TrustChip icon={LineChart} title="Board-Grade Insights" sub="CXO · KPIs · trends" color="#D55816" />
              <TrustChip icon={Layers} title="Full Drill-Down" sub="widget · record · evidence" color="#B27D49" />
            </div>
          </div>

          <div className="flex items-center justify-between text-[11.5px]" style={{ color: "var(--cv-light-grey)" }}>
            <div>© {new Date().getFullYear()} CyberVisible. All rights reserved.</div>
            <div className="flex items-center gap-4 tracking-[0.14em] uppercase font-bold" style={{ color: "var(--cv-dark-grey)" }}>
              <span>SOC 2 · Type II</span>
              <span>ISO 27001</span>
            </div>
          </div>
        </div>

        {/* -------------------------- Right: Form Card -------------------------- */}
        <div className="flex items-center justify-center p-6 sm:p-10 lg:p-14">
          <div
            className="w-full max-w-[460px] rounded-2xl p-8 sm:p-10 relative"
            style={{
              background: "#fff",
              border: "1px solid var(--cv-line)",
              boxShadow: "0 30px 80px rgba(127,95,82,0.18), 0 6px 18px rgba(127,95,82,0.08)",
            }}
            data-testid="login-card"
          >
            {/* top accent inside card */}
            <div
              aria-hidden
              className="absolute top-0 left-6 right-6 h-[3px] rounded-b-full"
              style={{
                background:
                  "linear-gradient(90deg, var(--cv-crimson) 0%, var(--cv-orange) 50%, var(--cv-gold) 100%)",
              }}
            />

            {/* mobile brand */}
            <div className="lg:hidden flex items-center gap-2 mb-6">
              <MonoMark size={36} />
              <div className="text-[18px] font-serif-display font-bold" style={{ color: "var(--cv-charcoal)" }}>
                Cyber<span style={{ color: "var(--cv-crimson)" }}>Visible</span>
              </div>
            </div>

            {!tempToken ? (
              <>
                <div className="flex items-center gap-2 text-[11px] tracking-[0.24em] uppercase font-bold"
                  style={{ color: "var(--cv-orange)" }}>
                  <ShieldCheck className="w-3.5 h-3.5" /> Secure sign-in
                </div>
                <h2 className="font-serif-display mt-2 leading-tight" style={{ fontSize: 34, color: "var(--cv-charcoal)" }}>
                  Welcome back.
                </h2>
                <p className="text-[13.5px] mt-2" style={{ color: "var(--cv-dark-grey)" }}>
                  Sign in to your CyberVisible workspace.
                </p>

                <form onSubmit={submitLogin} className="mt-7 space-y-4" data-testid="login-form">
                  <Field label="Work email">
                    <Input
                      data-testid="login-email" type="email" required autoFocus autoComplete="username"
                      value={email} onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@company.com" className="oem-input"
                    />
                  </Field>

                  <Field label="Password">
                    <div className="relative">
                      <Input
                        data-testid="login-password"
                        type={showPw ? "text" : "password"} required autoComplete="current-password"
                        value={password} onChange={(e) => setPassword(e.target.value)}
                        placeholder="••••••••••" className="oem-input pr-11"
                      />
                      <button
                        type="button" onClick={() => setShowPw((v) => !v)}
                        aria-label={showPw ? "Hide password" : "Show password"}
                        className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded"
                        style={{ color: "var(--cv-dark-grey)" }}
                      >
                        {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </Field>

                  <Field label="Verification">
                    <div className="flex items-center gap-2">
                      <div
                        data-testid="captcha-question"
                        className="px-3 h-11 rounded-lg font-serif-display text-[19px] tabular-nums flex items-center justify-center"
                        style={{
                          minWidth: 130, letterSpacing: 2,
                          background: "var(--cv-sand)",
                          color: "var(--cv-charcoal)",
                          border: "1px solid var(--cv-line)",
                        }}
                      >
                        {captcha ? captcha.question : "…"}
                      </div>
                      <Input
                        data-testid="captcha-answer" required inputMode="numeric" placeholder="Answer"
                        value={captchaAns} onChange={(e) => setCaptchaAns(e.target.value)}
                        className="oem-input flex-1"
                      />
                      <button
                        type="button" data-testid="captcha-refresh" onClick={refreshCaptcha}
                        aria-label="new captcha"
                        className="h-11 w-11 rounded-lg flex items-center justify-center transition-colors"
                        style={{
                          background: "var(--cv-sand)",
                          border: "1px solid var(--cv-line)",
                          color: "var(--cv-dark-grey)",
                        }}
                      >
                        <RefreshCcw className="w-4 h-4" />
                      </button>
                    </div>
                  </Field>

                  {err && (
                    <div
                      data-testid="login-error"
                      className="text-[13px] px-3 py-2 rounded-lg"
                      style={{
                        background: "rgba(165,48,15,0.10)",
                        border: "1px solid rgba(165,48,15,0.28)",
                        color: "var(--cv-crimson)",
                      }}
                    >
                      {err}
                    </div>
                  )}

                  <Button
                    data-testid="login-submit"
                    disabled={busy} type="submit"
                    className="w-full h-11 group text-[14.5px] font-bold tracking-wide"
                    style={{
                      background:
                        "linear-gradient(180deg, #C13818 0%, #A5300F 100%)",
                      color: "#fff",
                      border: "1px solid rgba(229,194,67,0.5)",
                      boxShadow: "0 10px 24px rgba(165,48,15,0.32), inset 0 1px 0 rgba(255,255,255,0.16)",
                    }}
                  >
                    {busy ? "Signing in…" : (
                      <span className="inline-flex items-center gap-2">
                        Sign in <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                      </span>
                    )}
                  </Button>

                  <div className="pt-2 text-[12px] text-center" style={{ color: "var(--cv-light-grey)" }}>
                    Trouble signing in? Contact your CyberVisible administrator.
                  </div>
                </form>
              </>
            ) : (
              <>
                <div className="flex items-center gap-2 text-[11px] tracking-[0.24em] uppercase font-bold"
                  style={{ color: "var(--cv-orange)" }}>
                  <ShieldCheck className="w-3.5 h-3.5" /> Two-Factor Authentication
                </div>
                <h2 className="font-serif-display mt-2 leading-tight" style={{ fontSize: 34, color: "var(--cv-charcoal)" }}>
                  Enter your code.
                </h2>
                <p className="text-[13.5px] mt-2" style={{ color: "var(--cv-dark-grey)" }}>
                  Open your authenticator app and enter the 6-digit code shown for CyberVisible.
                </p>

                <form onSubmit={submitOtp} className="mt-6 space-y-4" data-testid="otp-form">
                  <Input
                    data-testid="otp-code" required inputMode="numeric" pattern="[0-9]{6}" maxLength={6}
                    value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                    placeholder="123 456"
                    className="oem-input tracking-[0.45em] text-center h-14 font-serif-display"
                    style={{ fontSize: 22 }}
                  />
                  {err && (
                    <div data-testid="otp-error" className="text-[13px] px-3 py-2 rounded-lg"
                      style={{ background: "rgba(165,48,15,0.10)", border: "1px solid rgba(165,48,15,0.28)", color: "var(--cv-crimson)" }}>
                      {err}
                    </div>
                  )}
                  <Button
                    data-testid="otp-submit" disabled={busy || otp.length !== 6} type="submit"
                    className="w-full h-11 font-bold"
                    style={{
                      background: "linear-gradient(180deg, #C13818 0%, #A5300F 100%)",
                      color: "#fff",
                      border: "1px solid rgba(229,194,67,0.5)",
                      boxShadow: "0 10px 24px rgba(165,48,15,0.32), inset 0 1px 0 rgba(255,255,255,0.16)",
                    }}
                  >
                    {busy ? "Verifying…" : "Verify & continue"}
                  </Button>
                  <button
                    type="button" data-testid="otp-back"
                    onClick={() => { setTempToken(null); setOtp(""); setErr(null); refreshCaptcha(); }}
                    className="w-full text-[12px] tracking-[0.18em] uppercase font-bold"
                    style={{ color: "var(--cv-dark-grey)" }}
                  >
                    ← use a different account
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span
        className="block text-[11px] tracking-[0.22em] uppercase font-bold mb-1.5"
        style={{ color: "var(--cv-dark-grey)" }}
      >
        {label}
      </span>
      {children}
    </label>
  );
}
